/**
 * Centralized API configuration
 *
 * This module provides a single source of truth for API URL configuration.
 * All API calls should use `apiAxios` to ensure consistent behavior.
 *
 * In production: Uses VITE_API_URL (e.g., https://api.markeroo.com)
 * In development: Falls back to empty string (Vite proxies /api to backend)
 */
import axios from "axios";
import type { AxiosInstance } from "axios";

import { useForceUpdateStore } from "@/stores/forceUpdate";
import { useAuthStore } from "@/stores/auth";
import { sanitizeRedirect } from "@/lib/security/redirects";
import { isPublicRoute } from "@/lib/routes/publicRoutes";

// Base URL for the API server (without /api suffix)
// Production: https://api.markeroo.com
// Development: "" (empty, Vite proxy handles it)
export const API_ORIGIN = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

// Full API base URL including the /api path prefix
// Production: https://api.markeroo.com/api
// Development: /api (proxied by Vite)
export const API_BASE_URL = `${API_ORIGIN}/api`;

/**
 * Pre-configured axios instance for ALL API calls.
 *
 * Usage:
 * ```ts
 * import { apiAxios } from "@/api/config";
 *
 * // GET request
 * const response = await apiAxios.get("/auth/me");
 *
 * // POST request with data
 * const response = await apiAxios.post("/assignments", { title: "New" });
 *
 * // PATCH request
 * const response = await apiAxios.patch("/assignments/123", { title: "Updated" });
 *
 * // DELETE request
 * await apiAxios.delete("/assignments/123");
 * ```
 *
 * Features:
 * - Automatically prepends API_BASE_URL to all paths
 * - Includes credentials for cross-origin requests
 * - Parses JSON responses automatically
 * - Provides typed error handling with axios.isAxiosError()
 * - Checks X-Force-Update-At header for version updates
 */
export const apiAxios: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Flag to prevent multiple simultaneous redirects on concurrent 401 errors
let isRedirecting = false;

// Response interceptor to check for force update header and handle 401 errors
apiAxios.interceptors.response.use(
  (response) => {
    const forceUpdateHeader = response.headers["x-force-update-at"] as string | undefined;
    if (forceUpdateHeader) {
      const timestamp = parseInt(forceUpdateHeader, 10);
      if (!isNaN(timestamp)) {
        useForceUpdateStore.getState().checkForUpdate(timestamp);
      }
    }
    return response;
  },
  (err: unknown) => {
    // Also check on error responses (server might still send the header)
    if (axios.isAxiosError(err) && err.response?.headers?.["x-force-update-at"]) {
      const headerValue = err.response.headers["x-force-update-at"] as string;
      const timestamp = parseInt(headerValue, 10);
      if (!isNaN(timestamp)) {
        useForceUpdateStore.getState().checkForUpdate(timestamp);
      }
    }

    // Handle 401 Unauthorized - session expired or invalid
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      // Clear auth state
      const authStore = useAuthStore.getState();
      authStore.setUser(null);

      // Get current location to redirect back after login
      const currentPath = window.location.pathname + window.location.search;

      // Don't redirect if already on a public page or already redirecting
      if (!isPublicRoute(window.location.pathname) && !isRedirecting) {
        isRedirecting = true;

        // Sanitize the redirect target to prevent open redirect attacks
        const sanitizedPath = sanitizeRedirect(currentPath);
        const nextParam = sanitizedPath !== "/" ? `?next=${encodeURIComponent(sanitizedPath)}` : "";

        // Use setTimeout to avoid React state update warnings
        // The promise rejection will complete before the redirect executes
        setTimeout(() => {
          window.location.href = `/login${nextParam}`;
        }, 0);
      }
    }

    return Promise.reject(err instanceof Error ? err : new Error(String(err)));
  }
);
